import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowDownRight, ArrowUpRight } from "lucide-react";
import { MaskHeading, Reveal } from "@/components/site/Reveal";

const HeroLine = ({ lead, word, tone }) => (
  <>
    {lead} <em className={`italic font-light ${tone}`}>{word}</em>
  </>
);

const Wave = () => (
  <svg viewBox="0 0 600 40" className="mt-8 h-6 w-[min(520px,90%)]" aria-hidden="true">
    <defs>
      <linearGradient id="wv" x1="0" x2="1">
        <stop offset="0" stopColor="var(--sky)" />
        <stop offset="1" stopColor="var(--rose)" />
      </linearGradient>
    </defs>
    <motion.path
      d="M4 30 C 140 4, 260 4, 340 20 S 520 36, 596 8"
      fill="none"
      stroke="url(#wv)"
      strokeWidth="3"
      strokeLinecap="round"
      initial={{ pathLength: 0 }}
      animate={{ pathLength: 1 }}
      transition={{ duration: 1.6, delay: 0.9, ease: [0.65, 0, 0.35, 1] }}
    />
  </svg>
);

const Frame = ({ src, alt, className, style, delay, testId }) => (
  <motion.div
    className={`absolute overflow-hidden bg-[var(--paper-2)] shadow-[0_30px_60px_-30px_rgba(20,22,27,0.35)] ${className}`}
    style={style}
    initial={{ clipPath: "inset(100% 0 0 0)" }}
    animate={{ clipPath: "inset(0% 0 0 0)" }}
    transition={{ duration: 1.3, delay, ease: [0.76, 0, 0.24, 1] }}
    data-testid={testId}
  >
    <motion.img
      src={src}
      alt={alt}
      className="h-full w-full object-cover"
      initial={{ scale: 1.25 }}
      animate={{ scale: 1 }}
      transition={{ duration: 1.8, delay, ease: [0.22, 1, 0.36, 1] }}
    />
  </motion.div>
);

const Collage = () => {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [6, -6]), { stiffness: 80, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-8, 8]), { stiffness: 80, damping: 18 });
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y1 = useTransform(scrollYProgress, [0, 1], [0, -90]);
  const y2 = useTransform(scrollYProgress, [0, 1], [0, 60]);
  const y3 = useTransform(scrollYProgress, [0, 1], [0, -160]);

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const reset = () => { mx.set(0); my.set(0); };

  return (
    <div ref={ref} className="relative h-[460px] sm:h-[560px] lg:h-[640px] [perspective:1400px]" onMouseMove={onMove} onMouseLeave={reset} data-testid="hero-collage">
      <motion.div className="absolute inset-0 [transform-style:preserve-3d]" style={{ rotateX: rx, rotateY: ry }}>
        <motion.div className="absolute inset-0" style={{ y: y1 }}>
          <Frame src="/images/hero-beauty.jpg" alt="Beauty and skincare products arranged on a tray" className="arch left-[14%] top-0 h-[78%] w-[58%]" delay={0.35} testId="hero-frame-main" />
        </motion.div>
        <motion.div className="absolute inset-0" style={{ y: y2, translateZ: 60 }}>
          <Frame src="/images/hero-shelf.jpg" alt="Organised retail refrigerator shelves with packaged drinks" className="left-0 bottom-[2%] h-[40%] w-[36%] rounded-sm" delay={0.6} testId="hero-frame-shelf" />
        </motion.div>
        <motion.div className="absolute inset-0" style={{ y: y3, translateZ: 100 }}>
          <Frame src="/images/hero-bag.jpg" alt="Leather handbag styled with accessories" className="right-0 top-[38%] h-[44%] w-[38%] rounded-full" delay={0.8} testId="hero-frame-bag" />
        </motion.div>
        <motion.div
          className="absolute right-[4%] top-[6%] flex h-24 w-24 items-center justify-center rounded-full border border-ink/20 bg-paper/80 backdrop-blur-md sm:h-28 sm:w-28"
          style={{ translateZ: 140 }}
          initial={{ opacity: 0, scale: 0.6 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 1.3, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        >
          <motion.svg viewBox="0 0 100 100" className="absolute inset-0 h-full w-full" animate={{ rotate: 360 }} transition={{ duration: 28, repeat: Infinity, ease: "linear" }} aria-hidden="true">
            <defs><path id="circ" d="M50,50 m-38,0 a38,38 0 1,1 76,0 a38,38 0 1,1 -76,0" /></defs>
            <text fontSize="8.2" letterSpacing="2.4" fill="var(--ink)" fontFamily="JetBrains Mono, monospace">
              <textPath href="#circ">ONE GROUP · EIGHT BRANDS · </textPath>
            </text>
          </motion.svg>
          <span className="font-display text-2xl italic">8</span>
        </motion.div>
      </motion.div>
    </div>
  );
};

export const HeroSection = () => (
  <section className="relative overflow-hidden pt-28 md:pt-32" data-testid="hero-section">
    <div aria-hidden="true" className="pointer-events-none absolute -right-40 -top-40 h-[520px] w-[520px] rounded-full bg-brandsky/10 blur-3xl" />
    <div aria-hidden="true" className="pointer-events-none absolute -left-40 top-1/2 h-[420px] w-[420px] rounded-full bg-brandrose/10 blur-3xl" />
    <div className="container-x relative grid items-center gap-12 pb-16 lg:grid-cols-12 lg:gap-8 lg:pb-24">
      <div className="lg:col-span-7">
        <Reveal y={12}>
          <p className="eyebrow flex items-center gap-3"><span className="h-px w-10 bg-[var(--ink)]" /> Suvirika Retail Pvt Ltd · India</p>
        </Reveal>
        <MaskHeading
          as="h1"
          onLoad
          testId="hero-headline"
          className="font-display mt-8 text-[2.75rem] leading-[0.98] tracking-[-0.02em] sm:text-6xl lg:text-7xl xl:text-[5.6rem]"
          lines={[
            <HeroLine key="a" lead="Building" word="Brands." tone="text-sky-ink" />,
            <HeroLine key="b" lead="Connecting" word="Markets." tone="text-rose-ink" />,
            <span key="c">Creating Everyday</span>,
            <em key="d" className="italic font-light text-sky-ink">Value.</em>,
          ]}
        />
        <Wave />
        <Reveal delay={0.8} y={16}>
          <p className="mt-6 max-w-xl text-base leading-relaxed text-[var(--ink-2)] md:text-lg" data-testid="hero-subtext">
            Suvirika Retail Pvt Ltd is building a diversified portfolio of consumer and commerce businesses across distribution, beauty, FMCG, fashion, accessories, and everyday essentials.
          </p>
        </Reveal>
        <Reveal delay={0.95} y={16} className="mt-10 flex flex-wrap gap-4">
          <Link to="/brands" className="btn-ink" data-testid="hero-explore-brands-btn">Explore Our Brands <ArrowDownRight size={18} /></Link>
          <Link to="/partner-with-us" className="btn-line" data-testid="hero-partner-btn">Partner With Us <ArrowUpRight size={18} /></Link>
        </Reveal>
      </div>
      <div className="lg:col-span-5">
        <Collage />
      </div>
    </div>
  </section>
);
